import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './context/AuthContext.js';
import { getApiBase } from './backendProbe.js';

export default function AppleCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    // Prevent browser from showing "unsaved changes" warning
    window.history.replaceState(null, '', window.location.pathname);
    
    // Apple Sign In can send data via POST or URL params
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const idToken = urlParams.get('id_token');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    // Also check hash fragment (some OAuth flows use this)
    const hash = window.location.hash.substring(1);
    const hashParams = new URLSearchParams(hash);
    const hashIdToken = hashParams.get('id_token');
    const hashCode = hashParams.get('code');
    const hashError = hashParams.get('error');
    const hashErrorDescription = hashParams.get('error_description');
    const returnedState = urlParams.get('state') || hashParams.get('state');

    try {
      const expectedState = sessionStorage.getItem('apple_oauth_state');
      if (expectedState && returnedState && expectedState !== returnedState) {
        setIsProcessing(false);
        alert('Sign in with Apple could not be verified (session mismatch). Please try again.');
        navigate('/login', { replace: true });
        return;
      }
    } catch (_) {}

    // Check for errors first
    if (error || hashError) {
      const oauthError = error || hashError;
      const oauthErrorDesc = errorDescription || hashErrorDescription;
      let errorMessage = oauthErrorDesc || oauthError;
      
      // Provide helpful error messages for common issues
      if (oauthError === 'invalid_client') {
        errorMessage = 'Apple login is not properly configured. Please contact support or use another login method (Google, Microsoft, or Facebook).';
      } else if (oauthError === 'access_denied') {
        errorMessage = 'You cancelled the Apple login. Please try again if you want to continue.';
      }
      
      setIsProcessing(false);
      setTimeout(() => {
        alert(`Apple login failed: ${errorMessage}`);
        navigate('/login', { replace: true });
      }, 100);
      return;
    }

    try {
      sessionStorage.removeItem("apple_oauth_state");
      sessionStorage.removeItem("apple_oauth_nonce");
    } catch (_) {}

    const identityToken = idToken || hashIdToken;
    const authorizationCode = code || hashCode;

    if (identityToken) {
      // Send token to backend
      const base = getApiBase();
      const endpoint = base ? `${base}/auth/apple` : '/auth/apple';
      axios.post(endpoint, { 
        identityToken: identityToken,
        authorizationCode: authorizationCode 
      })
        .then((response) => {
          login(response.data.token, response.data.user);
          setIsProcessing(false);
          navigate('/chatbot', { replace: true });
        })
        .catch((err) => {
          console.error('Apple login failed', err);
          setIsProcessing(false);
          const errorMsg = err.response?.status === 403 && err.response?.data?.code === 'INVITE_ONLY'
            ? (err.response.data.message || "This app is invite-only. You don't have access.")
            : (err.response?.data?.message || 'Apple login failed. Please try again or use another login method.');
          setTimeout(() => {
            alert(errorMsg);
            navigate('/login', { replace: true });
          }, 100);
        });
    } else {
      console.error('No Apple id_token in callback URL. Register this exact Return URL in Apple Developer → Services ID → Return URLs:', `${window.location.origin}/auth/apple/callback`);
      setIsProcessing(false);
      alert(
        'Apple did not return a sign-in token. Confirm in Apple Developer that Return URL matches exactly:\n' +
          `${window.location.origin}/auth/apple/callback\n` +
          '(same scheme, host, port, path; no extra slash.)'
      );
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 100);
    }
  }, [navigate, login]);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontSize: '18px'
    }}>
      {isProcessing ? 'Processing Apple login...' : 'Redirecting...'}
    </div>
  );
}

